import { For, Show, type JSX } from "solid-js";

import clsx from "~/util/html/style";

import styles from "./art.module.css";
import { BONE_RIDGE, GUM_RIDGE, NECK_SPAN, Tissue, ToothBody } from "./tooth_art";

/** The gap scene's state: how far the neighbors lean toward the gap (degrees), how far the opposing tooth has grown
 *  down into the space, and how far the bone under the gap has sunk (units of the tooth drawing's y). */
export type GapSpec = { lean: number; drift: number; boneDrop: number };

// The lower arch as the bone graft drawing lays it out: ToothBody draws a tooth at x 60, so the gap sits there and a
// neighbor 92 to either side. Each neighbor tips toward the gap about its root tips.
const GAP = 60;
const NEIGHBORS = [
  { dx: -92, toward: 1 },
  { dx: 92, toward: -1 },
] as const;
const GUM = 84;
const BONE = 100;
// Tissue's own edge where a tooth's neck meets it: the gum line's level, and the bone crest's at the papilla tip, its
// control point and the neck. The gap's own lines start and end on them.
const GUM_NECK = GUM_RIDGE[3];
const [, BONE_TIP, BONE_SHOULDER, BONE_NECK] = BONE_RIDGE;

// The opposing tooth is the same drawing upside down: flipping its space about y 22 sets its cusps on the lower
// teeth's chewing surface. Its gum and bone stay put while it drifts.
const FLIP = 44;

// Every stage fits, nothing past the edges: the tissue runs edge to edge, and its fade (Tissue's vignette) is clear
// by y 195 below the lower teeth and y -151 above the upper one. Leaning moves the neighbors inward, and drifting
// moves the tooth across toward the middle.
const VIEW_BOX = "-90 -151 300 346";
const X0 = -90;
const X1 = 210;

type Point = readonly [x: number, y: number];
type Cubic = readonly [Point, Point, Point];
type Curve = { start: Point; cubics: readonly Cubic[] };

const f = (v: number) => Number(v.toFixed(2));
const at = ([x, y]: Point) => `${f(x)},${f(y)}`;

/**
 * The bone crest across the gap, from papilla tip to papilla tip, as a start and three cubics. With no drop it is the
 * tissue's own crest (Tissue's bone ridge); a drop sinks the middle by that much. `lift` raises everything between the
 * tips.
 */
function crest(drop: number, lift = 0): Curve {
  const [left, right] = [GAP - 46, GAP + 46];
  const [neckL, neckR] = [GAP - NECK_SPAN, GAP + NECK_SPAN];
  const tip = BONE + BONE_TIP;
  const shoulder = BONE + BONE_SHOULDER + drop / 4 - lift;
  const side = BONE + BONE_NECK + drop / 2 - lift;
  // A cubic's middle sits three quarters of the way from its ends to its control points' level.
  const low = BONE + BONE_NECK + (drop * 7) / 6 - lift;
  return {
    start: [left, tip],
    cubics: [
      [
        [left + 10, tip - lift],
        [neckL - 12, shoulder],
        [neckL, side],
      ],
      [
        [neckL + 12, low],
        [neckR - 12, low],
        [neckR, side],
      ],
      [
        [neckR + 12, shoulder],
        [right - 10, tip - lift],
        [right, tip],
      ],
    ],
  };
}

function forward({ start, cubics }: Curve): string {
  return [`M${at(start)}`, ...cubics.map((c) => `C${c.map(at).join(" ")}`)].join(" ");
}

/** The same curve drawn from its end back to its start, to close a shape against another. */
function backward({ start, cubics }: Curve): string {
  return cubics
    .map((c, i) => `C${at(c[1])} ${at(c[0])} ${at(cubics[i - 1]?.[2] ?? start)}`)
    .reverse()
    .join(" ");
}

// The gum where the bone has shrunk away under the gap: the tissue's gum color at the crest. Its top edge sits a
// little into the gum, over the tissue's own crest line, so no seam of bone shows between them.
const SUNKEN_GUM = "#EDA8A2";
const OVERLAP = 1.5;

/**
 * A missing lower tooth from the side: the two neighbors beside the gap and the upper tooth across from it, drawn
 * from the same tooth and tissue as the other pictures.
 */
export function GapArt(props: { spec: GapSpec; title: string }): JSX.Element {
  return (
    <svg
      viewBox={VIEW_BOX}
      class={clsx(styles["tooth-art"], styles["gap-scene"])}
      role="img"
      aria-label={props.title}
    >
      <title>{props.title}</title>
      <g transform={`translate(0 ${FLIP}) scale(1 -1)`}>
        <Tissue gum={GUM} bone={BONE} x0={X0} x1={X1} />
      </g>
      <g transform={`translate(0 ${FLIP + props.spec.drift}) scale(1 -1)`}>
        <ToothBody spec={{ pulp: "ok" }} />
      </g>
      <Tissue
        gum={GUM}
        bone={BONE}
        centers={[GAP + NEIGHBORS[0].dx, GAP, GAP + NEIGHBORS[1].dx]}
        x0={X0}
        x1={X1}
      />
      <Show when={props.spec.boneDrop > 0}>
        <path
          d={`${forward(crest(0, OVERLAP))} ${backward(crest(props.spec.boneDrop))} Z`}
          fill={SUNKEN_GUM}
        />
      </Show>
      {/* Tissue leaves the gum line and the crest bare where a tooth's neck would cover them, and over the
          gap nothing does. */}
      <path
        d={`M${GAP - NECK_SPAN},${GUM + GUM_NECK} L${GAP + NECK_SPAN},${GUM + GUM_NECK}`}
        fill="none"
        stroke="#FCE3E0"
        stroke-width="1.8"
        stroke-linecap="round"
      />
      <path
        d={forward(crest(props.spec.boneDrop))}
        fill="none"
        stroke="#D8C49F"
        stroke-width="1.4"
      />
      <For each={NEIGHBORS}>
        {(neighbor) => (
          <g
            transform={`translate(${neighbor.dx} 0) rotate(${neighbor.toward * props.spec.lean} 60 160)`}
          >
            <ToothBody spec={{ pulp: "ok" }} />
          </g>
        )}
      </For>
    </svg>
  );
}
